import React from 'react';
import request from 'superagent';
import { Reel } from '../reel/reel';
import { BigWin } from '../big-win/big-win';
import { Board, Config, Entry, blankEntry, cellKey, cells, clone, coinOptions, coinValue, deserialize, emptyBoard, findStops, makeCoin, projectEntries, projectPlans, scriptEntry, serialize } from './helpers';
import '../ff001-starstruck-lines/ff001-starstruck.css';
import './style.css';
const errorText = (e: any) => e?.response?.body?.error?.message || e?.error?.message || e?.message || 'Scenario request failed.';
const coinLabel = (value: string | number) => typeof value === 'number' ? `${value} × credit` : value.toUpperCase();
export default function Farm2Scenarios({ modelId }: { modelId: string }) {
  const [config, setConfig] = React.useState<Config>();
  const [entries, setEntries] = React.useState<Entry[]>([]);
  const [presets, setPresets] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState('');
  const [user, setUser] = React.useState(localStorage.getItem('userid') || '');
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    Promise.all(['get-config', 'get'].map(endpoint => request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/${endpoint}`).withCredentials().send({ modelId })))
      .then(([c, s]) => {
        if (cancelled) return;
        if (!c.body.success || !s.body.success) throw new Error('Unable to load Farm 2 scenarios.');
        setConfig(c.body.config); setPresets(s.body.scenarios);
        const entry = blankEntry(c.body.config);
        entry.reelIndexes = findStops(c.body.config, false, new Set(), 'quiet');
        setEntries([entry]);
      }).catch(e => { if (!cancelled) setError(errorText(e)); });
    return () => { cancelled = true; };
  }, [modelId]);
  const act = (action: () => void) => { try { action(); setError(''); setNotice(''); } catch (e) { setError(errorText(e)); } };
  if (!config) return <div>{error ? <p role="alert">{error}</p> : 'Loading Farm 2…'}</div>;
  const projection = projectEntries(entries, config);
  const update = (index: number, entry: Entry) => setEntries(current => current.map((old, i) => i === index ? entry : old));
  const applyStops = (index: number, reelIndexes: number[], force = false) => update(index, { reelIndexes, forceCoinSpinsTrigger: force, plans: [] });
  const send = async (endpoint: string) => {
    setBusy(true); setError(''); setNotice('');
    try {
      if (endpoint === 'play-user' && !user.trim()) throw new Error('Enter the player user ID.');
      const scenarios = serialize(entries, config);
      const payload = { modelId, scenarios, ...(endpoint === 'upsert' ? { _id: selected || undefined } : { userPublicKey: user.trim() }) };
      const { body } = await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/${endpoint}`).withCredentials().send(payload);
      if (!body.success) throw new Error(errorText(body));
      if (endpoint === 'upsert') {
        const id = body._id || selected;
        if (id) { setSelected(id); setPresets(current => [...current.filter(p => p._id !== id), { _id: id, scenarios: clone(scenarios) }]); }
      }
      setNotice(endpoint === 'upsert' ? 'Scenario saved.' : 'Scenario queued. Play the game with Boosted Spins off; start from base mode.');
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  return <main className="ff001-container farm2-scenarios">
    <h1>{modelId}</h1>
    <p>Build a base spin, trigger Free Spins, then choose Wild x2 and sticky Wild combinations. Eggs and Coin Spins appear only in base play.</p>
    <p>Use a player in base mode with Boosted Spins off. These scenarios preserve real feature rules and held coins.</p>
    <label>Scenario preset <select aria-label="Scenario preset" value={selected} onChange={e => act(() => {
      const id = e.target.value; if (!id) { setSelected(''); return; }
      const preset = presets.find(p => p._id === id);
      if (preset.gameInput && Object.values(preset.gameInput).some(Boolean)) throw new Error('This preset uses a different paid mode; it has not been changed.');
      const loaded = deserialize(preset.scenarios, config); setEntries(loaded); setSelected(id);
    })}><option value="">New scenario</option>{presets.map(p => <option key={p._id} value={p._id}>{p._id} ({p.scenarios.length} spins)</option>)}</select></label>
    {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    {entries.map((entry, index) => {
      const view = projection[index];
      const strips = view.free ? config.freeSpinsReels : config.baseSpinsReels;
      const coinCount = cells(view.window).filter(c => c.value === config.coinFeature.coin).length;
      let plans: ReturnType<typeof projectPlans> = []; let planError = '';
      try { plans = projectPlans(entry.plans, config); } catch (e) { planError = errorText(e); }
      const changeBoard = (step: number, r: number, row: number, value: string) => {
        const next = clone(entry.plans);
        const option = coinOptions(config, step === 0).find(v => String(v) === value);
        next[step][r][row] = option === undefined ? null : makeCoin(option, step);
        update(index, { ...entry, plans: next });
      };
      return <section className="spin" key={index} aria-label={`Spin ${index + 1}`}>
        <h2>Spin {index + 1} — {view.free ? 'Free Spins · Wild x2' : 'Base'}</h2>
        <p>{coinCount} coins · {view.remaining} Free Spins remaining after this spin</p>
        <div className="farm2-actions">
          {(['pot', 'coins', 'free'] as const).map(kind => <button key={kind} disabled={view.free && kind !== 'free'} onClick={() => act(() => applyStops(index, findStops(config, view.free, view.held, kind), kind === 'pot'))}>
            {kind === 'pot' ? 'Find 1–5 coins' : kind === 'coins' ? 'Find 6+ coins' : view.free ? 'Retrigger Free Spins' : 'Trigger Free Spins'}
          </button>)}
          <BigWin config={config} reelStrips={strips} rowsByReel={config.reelsSetup} reelIndexes={entry.reelIndexes} mode={view.free ? 'Free Spins' : 'Base'} onApply={stops => applyStops(index, stops)} />
        </div>
        <div className="reelContainer">{strips.map((strip, r) => <div key={r}>
          <Reel index={r} rows={config.reelsSetup[r]} reelIndex={entry.reelIndexes[r]} reelStrips={strip}
            visibleSymbols={view.window[r].map((s, row) => s === config.wild && view.free ? `Wild x2${view.held.has(cellKey(r, row)) ? ' (held)' : ''}` : s)}
            setReelIndex={({ dir, forcedIndex }) => {
              const stop = ((forcedIndex ?? entry.reelIndexes[r] + (dir || 0)) % strip.length + strip.length) % strip.length;
              if (stop === entry.reelIndexes[r]) return;
              applyStops(index, entry.reelIndexes.map((old, reel) => reel === r ? stop : old), entry.forceCoinSpinsTrigger);
            }} />
          {view.window[r].map((symbol, row) => <label className="farm2-symbol" key={row}>Row {row + 1}
            <select aria-label={`Spin ${index + 1} reel ${r + 1} row ${row + 1} symbol`} value={symbol} disabled={view.held.has(cellKey(r, row))}
              onChange={e => act(() => {
                const offset = strip.findIndex(s => s === e.target.value);
                const stop = (offset - row + strip.length) % strip.length;
                applyStops(index, entry.reelIndexes.map((old, reel) => reel === r ? stop : old), entry.forceCoinSpinsTrigger);
              })}>
              {Array.from(new Set([...strip, symbol])).map(s => <option key={s} value={s}>{s === config.wild ? view.free ? 'Wild x2' : 'Wild' : s}</option>)}
            </select>
          </label>)}
        </div>)}</div>
        <p>Symbol selection moves the reel to a real stop. Held Wild x2 cells remain fixed.</p>
        <label><input type="checkbox" aria-label={`Force pot trigger ${index + 1}`} checked={entry.forceCoinSpinsTrigger} disabled={coinCount < 1 || coinCount >= config.coinFeature.coinTrigger}
          onChange={e => update(index, { ...entry, forceCoinSpinsTrigger: e.target.checked, plans: [] })} />Force Coin Spins trigger (1–5 coins)</label>
        <label><input type="checkbox" aria-label={`Script Coin Spins ${index + 1}`} checked={entry.plans.length > 0}
          disabled={coinCount < config.coinFeature.coinTrigger && !(coinCount > 0 && entry.forceCoinSpinsTrigger)}
          onChange={e => act(() => update(index, e.target.checked ? scriptEntry(entry, config, view.window) : { ...entry, plans: [] }))} />Script Coin Spins</label>
        {entry.plans.length > 0 && <>
          <p>Choose entry coins and new landings per respin. Held coins cannot be replaced; the model applies Collect. Three finishing misses are added automatically when needed. Fill all 15 cells to test GRAND.</p>
          {planError && <p role="alert">{planError}</p>}
          {entry.plans.map((board: Board, step) => <fieldset key={step}><legend>{step === 0 ? 'Coin Spins entry' : `Coin Spin ${step}`} · {plans[step]?.remaining ?? '?'} respins left</legend>
            <div className="farm2-coin-grid">{config.reelsSetup.map((rows, r) => <div key={r}>{Array.from({ length: rows }, (_, row) => {
              const held = plans[step]?.held.has(cellKey(r, row));
              const coin = board[r][row];
              const fixedEntry = step === 0 && view.window[r][row] === config.coinFeature.coin;
              return <label className="farm2-coin" key={row}>Reel {r + 1}, row {row + 1}
                <select aria-label={`Spin ${index + 1} coin step ${step} reel ${r + 1} row ${row + 1}`} disabled={held}
                  value={held ? 'held' : coin ? String(coinValue(coin)) : ''} onChange={e => changeBoard(step, r, row, e.target.value)}>
                  {held ? <option value="held">Held coin</option> : <>
                    {!fixedEntry && <option value="">Empty</option>}
                    {coinOptions(config, step === 0).map(v => <option key={v} value={String(v)}>{coinLabel(v)}</option>)}
                  </>}
                </select>
              </label>;
            })}</div>)}</div>
          </fieldset>)}
          <button disabled={!!planError || plans[plans.length - 1]?.remaining === 0} onClick={() => update(index, { ...entry, plans: [...entry.plans, emptyBoard(config)] })}>Add Coin Spin</button>
          <button disabled={entry.plans.length <= 1} onClick={() => update(index, { ...entry, plans: entry.plans.slice(0, -1) })}>Remove last Coin Spin</button>
          <button disabled={!!planError || plans[plans.length - 1]?.remaining === 0} onClick={() => {
            const occupied = plans[plans.length - 1].occupied;
            const board = emptyBoard(config).map((reel, r) => reel.map((_, row) => occupied.has(cellKey(r, row)) ? null : makeCoin(coinOptions(config, false)[0], entry.plans.length)));
            update(index, { ...entry, plans: [...entry.plans, board] });
          }}>Fill board for GRAND</button>
        </>}
      </section>;
    })}
    <div className="farm2-actions">
      <button onClick={() => act(() => {
        const free = projection[projection.length - 1]?.remaining > 0;
        const next = blankEntry(config);
        // Re-project with a provisional entry to obtain the next spin's sticky cells.
        const nextView = projectEntries([...entries, next], config)[entries.length];
        next.reelIndexes = findStops(config, free, nextView.held, 'quiet');
        setEntries([...entries, next]);
      })}>{projection[projection.length - 1]?.remaining > 0 ? 'Add Free Spin' : 'Add Base Spin'}</button>
      <button disabled={entries.length <= 1} onClick={() => setEntries(entries.slice(0, -1))}>Remove last spin</button>
      <button onClick={() => act(() => { const e = blankEntry(config); e.reelIndexes = findStops(config, false, new Set(), 'quiet'); setEntries([e]); setSelected(''); })}>New</button>
    </div>
    <label>Player user ID <input aria-label="Player user ID" value={user} onChange={e => { setUser(e.target.value); localStorage.setItem('userid', e.target.value); }} /></label>
    <div className="farm2-actions"><button disabled={busy} onClick={() => send('upsert')}>Save</button><button disabled={busy} onClick={() => send('play-user')}>Play</button></div>
  </main>;
}
